import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ServicesCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const services = [
    {
      icon: 'ri-robot-2-fill',
      title: 'AI Clone Service',
      description: 'Create your digital twin that works 24/7 to engage leads and qualify prospects automatically.',
      features: ['24/7 Lead Engagement', 'Personalized Responses', 'Lead Qualification'],
      link: '/services/ai-clone',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: 'ri-customer-service-2-fill',
      title: 'AI Chatbots',
      description: 'Intelligent chatbots that handle inquiries, schedule appointments, and nurture leads.',
      features: ['Instant Responses', 'Appointment Scheduling', 'Lead Nurturing'],
      link: '/ai-tools/chatbots',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: 'ri-brain-fill',
      title: 'Lead Generation AI',
      description: 'Advanced AI algorithms identify and qualify high-quality leads from multiple sources.',
      features: ['Smart Lead Scoring', 'Multi-Channel Sourcing', 'Automated Follow-up'],
      link: '/services/lead-generation',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: 'ri-list-check-3',
      title: 'Listing Automation',
      description: 'Generate compelling property descriptions and optimize listings for maximum visibility.',
      features: ['AI-Generated Descriptions', 'SEO Optimization', 'Market Positioning'],
      link: '/services/listing-automation',
      color: 'from-orange-500 to-orange-600'
    },
    {
      icon: 'ri-line-chart-fill',
      title: 'Market Analytics',
      description: 'Real-time market insights, price predictions, and investment opportunity analysis.',
      features: ['Price Predictions', 'Market Trends', 'Investment Analysis'],
      link: '/services/market-analytics',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: 'ri-image-edit-fill',
      title: 'AI Staging',
      description: 'Virtual staging and property enhancement using advanced AI technology.',
      features: ['Virtual Furniture', 'Room Enhancement', 'Style Variations'],
      link: '/services/ai-staging',
      color: 'from-teal-500 to-teal-600'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(services.length / 3));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.ceil(services.length / 3)) % Math.ceil(services.length / 3));
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  const getVisibleServices = () => {
    const startIndex = currentSlide * 3;
    return services.slice(startIndex, startIndex + 3);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 font-sans">
            Comprehensive AI Solutions for Real Estate
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-sans">
            Discover our full suite of AI-powered tools designed to automate your workflow and accelerate your success.
          </p>
        </div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {getVisibleServices().map((service, index) => (
                <div
                  key={`${currentSlide}-${index}`}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group"
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <i className={`${service.icon} text-white text-2xl`}></i>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 font-sans">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed font-sans">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-2 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-700 font-sans">
                        <i className="ri-check-line text-green-500 mr-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={service.link}
                    className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${service.color} text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer font-sans`}
                  >
                    Learn More
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-gray-600"></i>
          </button>
          
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-line text-xl text-gray-600"></i>
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: Math.ceil(services.length / 3) }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors cursor-pointer ${
                  currentSlide === index ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <Link
            to="/services"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold text-lg rounded-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap cursor-pointer font-sans"
          >
            <i className="ri-rocket-fill mr-2"></i>
            Explore All Services
          </Link>
        </div>
      </div>
    </section>
  );
}